import { isCommunityUrl, isEventsUrl } from '@/lib/communityLinks'
import { useCommunityWebView } from '@/lib/CommunityWebViewContext'
import { resolveAppLink } from '@/lib/resolveAppLink'
import { useRouter } from 'expo-router'
import React from 'react'
import { Linking, Platform } from 'react-native'
import { Pressable } from 'react-native-gesture-handler'

interface ExternalLinkProps {
  url: string
  children: React.ReactNode
  className?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  onPress?: () => void | Promise<void>
  replace?: boolean
  /**
   * Wait ~150ms after `onPress` before navigating (in-app links only). Lets an
   * open popover/sheet finish closing first, so a `push` (which mounts a new
   * screen + header) doesn't flicker over the still-animating overlay on web.
   */
  deferNavigation?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

/**
 * A reusable component for external links that works on both web and mobile
 * 
 * On web: Uses native <a> tag for proper browser behavior (right-click menu, Ctrl+click, etc.)
 * On mobile: Uses Linking.openURL() to open in default browser
 * 
 * @param url - The external URL to navigate to
 * @param children - The content to display inside the link
 * @param className - Optional CSS class for web
 * @param target - Optional target attribute for web (defaults to '_self' for same tab)
 * @param onPress - Optional additional onPress handler
 */
export default function ExternalLink({ 
  url, 
  children, 
  className,
  target = '_self',
  onPress,
  replace = false,
  deferNavigation = false,
  onMouseEnter,
  onMouseLeave,
}: ExternalLinkProps) {
  const router = useRouter()
  const communityWebView = useCommunityWebView()

  const handlePress = async () => {
    const result = onPress?.()
    if (result instanceof Promise) await result

    const { href, openInApp } = resolveAppLink(url)

    const navigate = () => {
      if (Platform.OS === 'web') {
        if (target === '_blank') {
          window.open(url, '_blank')
        } else if (openInApp) {
          if (replace) {
            router.replace(href as any)
          } else {
            router.push(href as any)
          }
        } else {
          window.location.href = href
        }
      } else {
        if (openInApp) {
          if (replace) {
            router.replace(href as any)
          } else {
            router.push(href as any)
          }
        } else if (communityWebView && isCommunityUrl(url)) {
          // The Hang opens in the in-app autologin WebView instead of Safari.
          communityWebView.openCommunity(url)
        } else if (communityWebView && isEventsUrl(url)) {
          // Events route by tier: pro → WebView, member → browser (logged
          // in), non-member → native paywall.
          communityWebView.openEvent(url)
        } else {
          Linking.openURL(href)
        }
      }
    }

    // Defer only in-app navigation: the `push` that would otherwise flicker over
    // a closing popover on web needs the overlay to tear down first (~150ms).
    // External links (full page load / browser) navigate immediately.
    if (deferNavigation && openInApp) {
      setTimeout(navigate, 150)
    } else {
      navigate()
    }
  }

  if (Platform.OS === 'web') {
    return (
      <a 
        href={url}
        target={target}
        className={className}
        style={{ 
          textDecoration: 'none', 
          color: 'inherit',
          display: 'flex',
          alignItems: 'center'
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(e) => {
          // Allow default browser behavior for modifier keys
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            return // Let browser handle it
          }
          e.preventDefault()
          void handlePress()
        }}
      >
        {children}
      </a>
    )
  }

  return (
    <Pressable
      onPress={() => void handlePress()}
      style={({ pressed }) => ({ opacity: pressed ? 0.85 : 1 })}
    >
      {children}
    </Pressable>
  )
}
